import React, { useState, useEffect, useCallback } from 'react';
import { Pin, RotateCcw } from 'lucide-react';
import type { DailyValue, DriverDailyBreakdownDay } from '../../api';
// eslint-disable-next-line import/no-relative-packages
import { formatCurrencyRounded } from '../../../../utils/currency';

interface DailyCalendarGridProps {
  year: number;
  month: number; // 1-12
  values: DailyValue[];
  onChange: (day: number, amount: number | null) => void;
  onStatValueChange?: (day: number, adr: number | null, roomNights: number | null) => void;
  readOnly?: boolean;
  showStatMode?: boolean;              // When true: show ADR + RN inputs, revenue is computed
  currency?: string | null;            // ISO-4217; null → USD
  referenceData?: Map<number, number>; // day -> reference revenue amount
  referenceLabel?: string;             // e.g., "OTB", "RMS", "LY"
  roomNightsReferenceData?: Map<number, number>; // day -> reference room nights
  roomNightsReferenceLabel?: string;
  // Pin mode (daily-capable drivers: OTB/RMS/REVPAR): unentered days show the
  // LIVE driver value; entering a value pins the day; actual (already realized)
  // days are read-only.
  pinMode?: boolean;
  liveData?: Map<number, DriverDailyBreakdownDay['live']>; // day -> live driver values
  actualDays?: Set<number>;            // realized days (read-only in pin mode)
  onUnpin?: (day: number) => void;     // revert a pinned day to live
}

// Returns '' for null/undefined so empty cells render blank, otherwise delegates
// to the shared currency formatter.
function formatCurrency(value: number | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined) return '';
  return formatCurrencyRounded(value, currency);
}

// Parse currency string to number
function parseCurrency(value: string): number | null {
  if (!value || value.trim() === '') return null;
  // Remove currency symbols, commas, spaces
  const cleaned = value.replace(/[$,\s]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

// Get the first day of the month (0=Sunday, 1=Monday, etc.)
function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month - 1, 1).getDay();
}

// Get number of days in the month
function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

interface DayCellProps {
  day: number;
  value: DailyValue | undefined;
  onChange: (amount: number | null) => void;
  onStatValueChange?: (adr: number | null, roomNights: number | null) => void;
  readOnly: boolean;
  showStatMode?: boolean;
  currency?: string | null;
  referenceValue?: number;
  referenceLabel?: string;
  roomNightsReferenceValue?: number;
  roomNightsReferenceLabel?: string;
  pinMode?: boolean;
  liveValue?: DriverDailyBreakdownDay['live'];
  isActualDay?: boolean;
  onUnpin?: () => void;
}

function DayCell({
  day,
  value,
  onChange,
  onStatValueChange,
  readOnly,
  showStatMode,
  currency,
  referenceValue,
  referenceLabel,
  roomNightsReferenceValue,
  roomNightsReferenceLabel,
  pinMode = false,
  liveValue,
  isActualDay = false,
  onUnpin,
}: DayCellProps) {
  const fmt = (v: number | null | undefined) => formatCurrency(v, currency);
  const [localValue, setLocalValue] = useState<string>(
    value?.amount !== null && value?.amount !== undefined
      ? value.amount.toString()
      : ''
  );
  const [localAdr, setLocalAdr] = useState<string>(
    value?.adr !== null && value?.adr !== undefined ? value.adr.toString() : ''
  );
  const [localRN, setLocalRN] = useState<string>(
    value?.roomNights !== null && value?.roomNights !== undefined
      ? value.roomNights.toString()
      : ''
  );
  const [isFocused, setIsFocused] = useState(false);
  const [isAdrFocused, setIsAdrFocused] = useState(false);
  const [isRNFocused, setIsRNFocused] = useState(false);

  // Sync local value when prop changes (standard mode)
  useEffect(() => {
    if (!isFocused && !showStatMode) {
      setLocalValue(
        value?.amount !== null && value?.amount !== undefined
          ? value.amount.toString()
          : ''
      );
    }
  }, [value?.amount, isFocused, showStatMode]);

  // Sync stat mode values when props change
  useEffect(() => {
    if (!isAdrFocused) {
      setLocalAdr(
        value?.adr !== null && value?.adr !== undefined ? value.adr.toString() : ''
      );
    }
  }, [value?.adr, isAdrFocused]);

  useEffect(() => {
    if (!isRNFocused) {
      setLocalRN(
        value?.roomNights !== null && value?.roomNights !== undefined
          ? value.roomNights.toString()
          : ''
      );
    }
  }, [value?.roomNights, isRNFocused]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    const parsed = parseCurrency(localValue);
    if (parsed !== value?.amount) {
      onChange(parsed);
    }
    if (parsed !== null) {
      setLocalValue(parsed.toString());
    }
  }, [localValue, value?.amount, onChange]);

  const handleAdrBlur = useCallback(() => {
    setIsAdrFocused(false);
    const parsedAdr = parseCurrency(localAdr);
    const parsedRN = localRN !== '' ? parseInt(localRN, 10) : null;
    if (!isNaN(parsedRN as number) || parsedRN === null) {
      if (parsedAdr !== value?.adr || parsedRN !== value?.roomNights) {
        onStatValueChange?.(parsedAdr, parsedRN);
      }
    }
  }, [localAdr, localRN, value?.adr, value?.roomNights, onStatValueChange]);

  const handleRNBlur = useCallback(() => {
    setIsRNFocused(false);
    const parsedAdr = localAdr !== '' ? parseCurrency(localAdr) : null;
    const parsedRN = localRN !== '' ? parseInt(localRN, 10) : null;
    const validRN = parsedRN !== null && !isNaN(parsedRN) ? parsedRN : null;
    if (parsedAdr !== value?.adr || validRN !== value?.roomNights) {
      onStatValueChange?.(parsedAdr, validRN);
    }
  }, [localAdr, localRN, value?.adr, value?.roomNights, onStatValueChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.currentTarget.blur();
      } else if (e.key === 'Escape') {
        setLocalValue(
          value?.amount !== null && value?.amount !== undefined
            ? value.amount.toString()
            : ''
        );
        e.currentTarget.blur();
      }
    },
    [value?.amount]
  );

  const isWeekend = value?.isWeekend || false;
  const hasValue = value?.hasValue || false;
  const hasStatValues = value?.hasStatValues || false;
  const isPinned = pinMode && (showStatMode ? hasStatValues : hasValue);

  // Saved/imported revenue is authoritative; ADR may be display-rounded or
  // unavailable when the source has revenue with zero room nights.
  const computedRevenue =
    showStatMode ? (value?.amount ?? (value?.adr != null && value?.roomNights != null
      ? value.adr * value.roomNights : null)) : null;

  // Pin mode: realized days are read-only — the driver reflects actuals there
  if (pinMode && isActualDay) {
    return (
      <div className={`relative border-r border-b border-slate-200 p-1 ${showStatMode ? 'min-h-[120px]' : 'min-h-[70px]'} bg-slate-50`}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-slate-400">{day}</span>
          <span className="text-[9px] uppercase tracking-wide text-slate-400 bg-slate-100 rounded px-1">
            Actual
          </span>
        </div>
        <div className="w-full text-right text-sm px-1 py-0.5 text-slate-500 font-medium">
          {liveValue?.amount != null ? fmt(liveValue.amount) : '—'}
        </div>
        {showStatMode && liveValue?.rooms != null && (
          <div className="text-[10px] text-slate-400 text-right">
            {Math.round(liveValue.rooms)} RN
            {liveValue.adr != null ? ` · ${fmt(liveValue.adr)}` : ''}
          </div>
        )}
      </div>
    );
  }

  // Pin/live affordances shared by both editable modes
  const pinHeader = pinMode ? (
    isPinned ? (
      <span className="flex items-center gap-1">
        <Pin size={10} className="text-amber-600 fill-amber-500" />
        {onUnpin && (
          <button
            onClick={onUnpin}
            title="Revert this day to the live driver value"
            className="text-slate-400 hover:text-amber-700 transition-colors"
          >
            <RotateCcw size={10} />
          </button>
        )}
      </span>
    ) : (
      <span
        className="text-[9px] uppercase tracking-wide text-sky-600 bg-sky-50 rounded px-1"
        title={`Live from driver${liveValue?.source ? ` (${liveValue.source.toUpperCase()})` : ''} — enter a value to pin this day`}
      >
        Live
      </span>
    )
  ) : null;

  if (showStatMode) {
    return (
      <div
        className={`
          relative border-r border-b border-slate-200 p-1 min-h-[120px]
          ${isWeekend ? 'bg-slate-50' : 'bg-white'}
          ${isPinned ? 'ring-1 ring-inset ring-amber-300' : hasStatValues ? 'ring-1 ring-inset ring-indigo-200' : ''}
        `}
      >
        {/* Day number + day of week */}
        <div className="flex items-center justify-between mb-1">
          <span className={`text-xs font-medium ${isWeekend ? 'text-slate-500' : 'text-slate-700'}`}>
            {day}
          </span>
          <span className="flex items-center gap-1">
            {pinHeader}
            {value?.dayOfWeek && (
              <span className="text-[10px] text-slate-400">{value.dayOfWeek}</span>
            )}
          </span>
        </div>

        {/* ADR input */}
        {!readOnly ? (
          <div className="flex items-center gap-0.5 mb-1">
            <span className="text-[10px] text-slate-400 w-7 flex-shrink-0">ADR</span>
            <input
              type="text"
              value={isAdrFocused ? localAdr : (value?.adr != null ? fmt(value.adr) : '')}
              onChange={(e) => setLocalAdr(e.target.value)}
              onFocus={() => setIsAdrFocused(true)}
              onBlur={handleAdrBlur}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              placeholder={pinMode && liveValue?.adr != null ? fmt(liveValue.adr) : '$0'}
              disabled={readOnly}
              className={`
                flex-1 min-w-0 text-right text-xs px-1 py-0.5 rounded border
                ${isPinned
                  ? 'border-amber-300 bg-amber-50 text-amber-900'
                  : hasStatValues
                    ? 'border-green-300 bg-green-50 text-green-900'
                    : 'border-slate-200 bg-white text-slate-600'
                }
                focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500
              `}
            />
          </div>
        ) : (
          <div className="flex items-center gap-0.5 mb-1">
            <span className="text-[10px] text-slate-400 w-7 flex-shrink-0">ADR</span>
            <div className={`flex-1 text-right text-xs px-1 py-0.5 ${hasStatValues ? 'text-green-900 font-medium' : 'text-slate-300'}`}>
              {value?.adr != null ? fmt(value.adr) : '—'}
            </div>
          </div>
        )}

        {/* Room Nights input */}
        {!readOnly ? (
          <div className="flex items-center gap-0.5 mb-1">
            <span className="text-[10px] text-slate-400 w-7 flex-shrink-0">RN</span>
            <input
              type="number"
              value={isRNFocused ? localRN : (value?.roomNights != null ? value.roomNights.toString() : '')}
              onChange={(e) => setLocalRN(e.target.value)}
              onFocus={() => setIsRNFocused(true)}
              onBlur={handleRNBlur}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              placeholder={pinMode && liveValue?.rooms != null ? String(Math.round(liveValue.rooms)) : '0'}
              disabled={readOnly}
              min="0"
              className={`
                flex-1 min-w-0 text-right text-xs px-1 py-0.5 rounded border
                ${isPinned
                  ? 'border-amber-300 bg-amber-50 text-amber-900'
                  : hasStatValues
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                    : 'border-slate-200 bg-white text-slate-600'
                }
                focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500
              `}
            />
          </div>
        ) : (
          <div className="flex items-center gap-0.5 mb-1">
            <span className="text-[10px] text-slate-400 w-7 flex-shrink-0">RN</span>
            <div className={`flex-1 text-right text-xs px-1 py-0.5 ${hasStatValues ? 'text-indigo-900 font-medium' : 'text-slate-300'}`}>
              {value?.roomNights != null ? value.roomNights : '—'}
            </div>
          </div>
        )}

        {/* Computed revenue (live driver revenue when the day isn't pinned) */}
        <div className={`text-[10px] text-right ${pinMode && computedRevenue == null ? 'text-sky-600' : 'text-slate-500'}`}>
          {computedRevenue != null
            ? fmt(computedRevenue)
            : pinMode && liveValue?.amount != null
              ? fmt(liveValue.amount)
              : '—'}
        </div>

        {/* Room nights reference */}
        {roomNightsReferenceValue !== undefined && roomNightsReferenceValue !== null && (
          <div className="mt-0.5 text-[9px] text-slate-400 text-right truncate">
            {roomNightsReferenceLabel}: {roomNightsReferenceValue} RN
          </div>
        )}
      </div>
    );
  }

  // Standard mode
  return (
    <div
      className={`
        relative border-r border-b border-slate-200 p-1 min-h-[70px]
        ${isWeekend ? 'bg-slate-50' : 'bg-white'}
        ${isPinned ? 'ring-1 ring-inset ring-amber-300' : hasValue ? 'ring-1 ring-inset ring-blue-200' : ''}
      `}
    >
      {/* Day number */}
      <div className="flex items-center justify-between mb-1">
        <span
          className={`
            text-xs font-medium
            ${isWeekend ? 'text-slate-500' : 'text-slate-700'}
          `}
        >
          {day}
        </span>
        <span className="flex items-center gap-1">
          {pinHeader}
          {value?.dayOfWeek && (
            <span className="text-[10px] text-slate-400">{value.dayOfWeek}</span>
          )}
        </span>
      </div>

      {/* Input field */}
      {!readOnly ? (
        <input
          type="text"
          value={isFocused ? localValue : fmt(parseCurrency(localValue))}
          onChange={(e) => setLocalValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={pinMode && liveValue?.amount != null ? fmt(liveValue.amount) : '$0'}
          className={`
            w-full text-right text-sm px-1 py-0.5 rounded border
            ${isPinned
              ? 'border-amber-300 bg-amber-50 text-amber-900'
              : hasValue
                ? 'border-blue-300 bg-blue-50 text-blue-900'
                : 'border-slate-200 bg-white text-slate-600'
            }
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          `}
        />
      ) : (
        <div
          className={`
            w-full text-right text-sm px-1 py-0.5
            ${hasValue ? 'text-slate-900 font-medium' : 'text-slate-400'}
          `}
        >
          {hasValue ? fmt(value?.amount) : '—'}
        </div>
      )}

      {/* Reference value (if provided) */}
      {referenceValue !== undefined && referenceValue !== null && (
        <div className="mt-1 text-[10px] text-slate-400 text-right truncate">
          {referenceLabel}: {fmt(referenceValue)}
        </div>
      )}
    </div>
  );
}

export function DailyCalendarGrid({
  year,
  month,
  values,
  onChange,
  onStatValueChange,
  readOnly = false,
  showStatMode = false,
  currency,
  referenceData,
  referenceLabel,
  roomNightsReferenceData,
  roomNightsReferenceLabel,
  pinMode = false,
  liveData,
  actualDays,
  onUnpin,
}: DailyCalendarGridProps) {
  // Closure over the prop so call sites stay terse — see DayCell below.
  const fmt = (value: number | null | undefined) => formatCurrency(value, currency);
  const firstDay = getFirstDayOfMonth(year, month);
  const daysInMonth = getDaysInMonth(year, month);

  // Create value map for quick lookup
  const valueMap = new Map<number, DailyValue>();
  values.forEach((v) => valueMap.set(v.day, v));

  // Build calendar grid
  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = [];

  // Add empty cells for days before the first of the month
  for (let i = 0; i < firstDay; i++) {
    currentWeek.push(null);
  }

  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  // Add empty cells for remaining days in the last week
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  // Calculate totals. In pin mode the monthly total blends pinned days with
  // live driver days; RN blends pinned room nights with live rooms.
  const isEntered = (v: DailyValue) => (showStatMode ? v.hasStatValues : v.hasValue);
  let monthlyRevenue = 0;
  let monthlyRN = 0;
  let liveDayCount = 0;
  if (pinMode && liveData) {
    for (let day = 1; day <= daysInMonth; day++) {
      const v = valueMap.get(day);
      const live = liveData.get(day);
      if (v && isEntered(v) && !actualDays?.has(day)) {
        monthlyRevenue += v.amount || 0;
        monthlyRN += v.roomNights ?? (live?.rooms || 0);
      } else {
        monthlyRevenue += live?.amount || 0;
        monthlyRN += live?.rooms || 0;
        liveDayCount++;
      }
    }
  } else {
    monthlyRevenue = values.reduce((sum, v) => sum + (v.amount || 0), 0);
    monthlyRN = showStatMode ? values.reduce((sum, v) => sum + (v.roomNights || 0), 0) : 0;
  }
  const weightedADR = showStatMode && monthlyRN > 0 ? monthlyRevenue / monthlyRN : 0;
  const daysWithValues = values.filter(isEntered).length;

  const emptyMinHeight = showStatMode ? 'min-h-[120px]' : 'min-h-[70px]';

  return (
    <div className="fv3-calendar bg-white rounded-lg border border-slate-200 overflow-hidden">
      {/* Header row with day names */}
      <div className="grid grid-cols-7 bg-slate-100 border-b border-slate-200">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className={`
              py-2 text-center text-xs font-semibold
              ${day === 'Sun' || day === 'Sat' ? 'text-slate-500 bg-slate-50' : 'text-slate-700'}
            `}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {weeks.map((week, weekIndex) =>
          week.map((day, dayIndex) =>
            day === null ? (
              <div
                key={`empty-${weekIndex}-${dayIndex}`}
                className={`border-r border-b border-slate-200 bg-slate-50 ${emptyMinHeight}`}
              />
            ) : (
              <DayCell
                key={day}
                day={day}
                value={valueMap.get(day)}
                onChange={(amount) => onChange(day, amount)}
                onStatValueChange={showStatMode ? (adr, rn) => onStatValueChange?.(day, adr, rn) : undefined}
                readOnly={readOnly}
                showStatMode={showStatMode}
                currency={currency}
                referenceValue={referenceData?.get(day)}
                referenceLabel={referenceLabel}
                roomNightsReferenceValue={roomNightsReferenceData?.get(day)}
                roomNightsReferenceLabel={roomNightsReferenceLabel}
                pinMode={pinMode}
                liveValue={liveData?.get(day)}
                isActualDay={actualDays?.has(day) || false}
                onUnpin={onUnpin ? () => onUnpin(day) : undefined}
              />
            )
          )
        )}
      </div>

      {/* Summary footer */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200">
        <div className="text-sm text-slate-600">
          {pinMode ? (
            <>
              <span className="font-medium text-amber-700">{daysWithValues}</span> pinned ·{' '}
              <span className="font-medium text-sky-600">{liveDayCount}</span> live
            </>
          ) : (
            <>
              <span className="font-medium">{daysWithValues}</span> of{' '}
              <span className="font-medium">{daysInMonth}</span> days entered
            </>
          )}
        </div>
        {showStatMode ? (
          <div className="flex items-center gap-6 text-right">
            <div>
              <div className="text-xs text-slate-500">Monthly RN</div>
              <div className="text-base font-semibold text-indigo-700">{monthlyRN.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Avg ADR</div>
              <div className="text-base font-semibold text-green-700">
                {weightedADR > 0 ? fmt(weightedADR) : '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Monthly Revenue</div>
              <div className="text-lg font-semibold text-slate-900">{fmt(monthlyRevenue)}</div>
            </div>
          </div>
        ) : (
          <div className="text-right">
            <div className="text-xs text-slate-500">Monthly Total</div>
            <div className="text-lg font-semibold text-slate-900">
              {fmt(monthlyRevenue)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
